import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { analytics } from '../services/api';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer,
  BarChart, Bar, PieChart, Pie, Cell
} from 'recharts';
import {
  TrendingUp, ShoppingBag, CreditCard, MapPin, Package, BarChart3, Tag, Ruler
} from 'lucide-react';

const COLORS = ['#4f7df9', '#2dd4a0', '#f5a623', '#9d7aff', '#ef5a6f', '#5be5d5', '#ff8a65', '#7986cb'];

const Analytics: React.FC = () => {
  const now = new Date();
  const firstOfMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
  const today = now.toISOString().split('T')[0];

  const [startDate, setStartDate] = useState(firstOfMonth);
  const [endDate, setEndDate] = useState(today);
  const [summary, setSummary] = useState<any>(null);
  const [trend, setTrend] = useState<any[]>([]);
  const [stateWise, setStateWise] = useState<any[]>([]);
  const [topSelling, setTopSelling] = useState<{ top_categories: any[]; top_products: any[] } | null>(null);
  const [topSizes, setTopSizes] = useState<any[]>([]);
  const [tsDays, setTsDays] = useState(7);

  const getTsRange = (days: number) => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - days + 1);
    return { start: start.toISOString().split('T')[0], end: end.toISOString().split('T')[0] };
  };

  useEffect(() => {
    analytics.summary(startDate, endDate).then(r => setSummary(r.data)).catch(() => {});
    analytics.revenueTrend(startDate, endDate).then(r => setTrend(r.data)).catch(() => {});
    analytics.stateWise(startDate, endDate).then(r => setStateWise(r.data)).catch(() => {});
  }, [startDate, endDate]);

  useEffect(() => {
    const { start, end } = getTsRange(tsDays);
    setTopSelling(null);
    analytics.topSelling(start, end).then(r => setTopSelling(r.data)).catch(() => {});
    analytics.topSizes(start, end).then(r => setTopSizes(r.data)).catch(() => {});
  }, [tsDays]);

  const isCurrentMonthView = startDate === firstOfMonth && endDate === today;

  const daysElapsed = isCurrentMonthView ? new Date().getDate() : 0;
  const daysInMonth = isCurrentMonthView
    ? new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).getDate()
    : 0;
  const daysLeft = daysInMonth - daysElapsed;
  const projectedRevenue = isCurrentMonthView && daysElapsed > 0 && summary
    ? Math.round((summary.revenue / daysElapsed) * daysInMonth)
    : null;
  const projectedOrders = isCurrentMonthView && daysElapsed > 0 && summary
    ? Math.round((summary.active_orders / daysElapsed) * daysInMonth)
    : null;

  const [sizePopup, setSizePopup] = useState<string | null>(null);
  const [sizeData, setSizeData] = useState<any[]>([]);
  const [sizeLoading, setSizeLoading] = useState(false);

  const openSizePopup = (product: string) => {
    setSizePopup(product);
    setSizeData([]);
    setSizeLoading(true);
    const { start, end } = getTsRange(tsDays);
    analytics.productSizes(product, start, end)
      .then(r => setSizeData(r.data))
      .catch(() => setSizeData([]))
      .finally(() => setSizeLoading(false));
  };

  const inputStyle: React.CSSProperties = {
    padding: '9px 14px', background: 'var(--bg-card)', border: '1px solid var(--border)',
    borderRadius: 'var(--radius-sm)', color: 'var(--text-primary)', fontSize: 13, outline: 'none',
    fontFamily: 'inherit',
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null;
    return (
      <div style={{
        background: 'var(--bg-card)', border: '1px solid var(--border)',
        borderRadius: 'var(--radius-sm)', padding: '10px 14px',
        boxShadow: 'var(--shadow-elevated)',
      }}>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>{label}</div>
        {payload.map((p: any, i: number) => (
          <div key={i} style={{ fontSize: 13, fontWeight: 600, color: p.color }}>
            {p.name}: {p.name === 'revenue' ? `₹${Number(p.value).toLocaleString('en-IN')}` : p.value}
          </div>
        ))}
      </div>
    );
  };

  // Quick range presets
  const setRange = (days: number) => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - days);
    setStartDate(start.toISOString().split('T')[0]);
    setEndDate(end.toISOString().split('T')[0]);
  };

  const setThisMonth = () => {
    const n = new Date();
    const y = n.getFullYear();
    const m = String(n.getMonth() + 1).padStart(2, '0');
    const d = String(n.getDate()).padStart(2, '0');
    setStartDate(`${y}-${m}-01`);
    setEndDate(`${y}-${m}-${d}`);
  };

  const setLastMonth = () => {
    const n = new Date();
    const first = new Date(n.getFullYear(), n.getMonth() - 1, 1);
    const last = new Date(n.getFullYear(), n.getMonth(), 0);
    setStartDate(first.toISOString().split('T')[0]);
    setEndDate(last.toISOString().split('T')[0]);
  };

  const paymentPieData = summary ? [
    { name: 'Prepaid', value: summary.prepaid_orders, color: '#4f7df9' },
    { name: 'COD', value: summary.cod_orders, color: '#f5a623' },
  ].filter(d => d.value > 0) : [];

  return (
    <Layout title="Sales Analytics" subtitle="Insights & performance metrics">
      {/* Date Range Bar */}
      <div className="animate-in" style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 22,
        flexWrap: 'wrap', gap: 10,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} style={inputStyle} />
          <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>to</span>
          <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} max={today} style={inputStyle} />
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {[
            { label: '7D', fn: () => setRange(7) },
            { label: '15D', fn: () => setRange(15) },
            { label: '30D', fn: () => setRange(30) },
            { label: 'This Month', fn: setThisMonth },
            { label: 'Last Month', fn: setLastMonth },
          ].map(p => (
            <button key={p.label} onClick={p.fn} style={{
              padding: '7px 14px', background: 'var(--bg-card)', border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)', color: 'var(--text-secondary)', fontSize: 12,
              fontWeight: 500, cursor: 'pointer', transition: 'var(--transition)',
            }}
              onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = 'var(--accent-blue)'; (e.currentTarget as HTMLElement).style.color = 'var(--accent-blue)'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = 'var(--border)'; (e.currentTarget as HTMLElement).style.color = 'var(--text-secondary)'; }}
            >{p.label}</button>
          ))}
        </div>
      </div>

      {/* Summary KPIs */}
      {summary && (
        <div className="animate-in-1" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 12, marginBottom: 22 }}>
          {[
            { label: 'Total Orders', value: summary.total_orders, color: 'var(--text-primary)', icon: <ShoppingBag size={15} />, sub: null },
            { label: 'Items Ordered', value: summary.total_items, color: 'var(--text-primary)', icon: <Package size={15} />, sub: null },
            { label: 'Active Orders', value: summary.active_orders, color: 'var(--accent-green)', icon: <Package size={15} />, sub: null },
            { label: 'Revenue', value: `₹${summary.revenue.toLocaleString('en-IN')}`, color: 'var(--accent-amber)', icon: <TrendingUp size={15} />, sub: null },
            ...(projectedRevenue !== null ? [{ label: 'Proj. Revenue', value: `₹${projectedRevenue.toLocaleString('en-IN')}`, color: 'var(--accent-amber)', icon: <TrendingUp size={15} />, sub: `est. month-end · ${daysLeft}d left` }] : []),
            ...(projectedOrders !== null ? [{ label: 'Proj. Orders', value: projectedOrders, color: 'var(--accent-blue)', icon: <ShoppingBag size={15} />, sub: `est. month-end · ${daysLeft}d left` }] : []),
            { label: 'Avg Order', value: `₹${summary.avg_order_value.toLocaleString('en-IN')}`, color: 'var(--accent-purple)', icon: <BarChart3 size={15} />, sub: null },
            { label: 'COD / Prepaid', value: `${summary.cod_orders} / ${summary.prepaid_orders}`, color: 'var(--accent-blue)', icon: <CreditCard size={15} />, sub: null },
          ].map((k, i) => (
            <div key={i} style={{
              background: 'var(--bg-card)', border: '1px solid var(--border)',
              borderRadius: 'var(--radius-md)', padding: '16px 18px',
            }}>
              <div style={{ color: 'var(--text-muted)', marginBottom: 10 }}>{k.icon}</div>
              <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 6, fontWeight: 600 }}>{k.label}</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: k.color, letterSpacing: -0.5 }}>{k.value}</div>
              {k.sub && <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 4 }}>{k.sub}</div>}
            </div>
          ))}
        </div>
      )}

      {/* Charts Row */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 16, marginBottom: 22 }}>
        {/* Revenue Trend */}
        <div className="animate-in-2" style={{
          background: 'var(--bg-card)', border: '1px solid var(--border)',
          borderRadius: 'var(--radius-lg)', padding: '20px 20px 10px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 18 }}>
            <TrendingUp size={15} color="var(--accent-green)" />
            <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>Revenue Trend</span>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={trend}>
              <defs>
                <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#4f7df9" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#4f7df9" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="date" tick={{ fill: '#4d506b', fontSize: 10 }} axisLine={false} tickLine={false}
                tickFormatter={v => { const d = new Date(v); return `${d.getDate()}/${d.getMonth()+1}`; }} />
              <YAxis tick={{ fill: '#4d506b', fontSize: 10 }} axisLine={false} tickLine={false}
                tickFormatter={v => `₹${(v/1000).toFixed(0)}k`} />
              <Tooltip content={<CustomTooltip />} />
              <Area type="monotone" dataKey="revenue" stroke="#4f7df9" strokeWidth={2} fill="url(#revGrad)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* COD vs Prepaid Pie */}
        <div className="animate-in-3" style={{
          background: 'var(--bg-card)', border: '1px solid var(--border)',
          borderRadius: 'var(--radius-lg)', padding: 20,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            <CreditCard size={15} color="var(--accent-blue)" />
            <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>Payment Split</span>
          </div>
          {paymentPieData.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={160}>
                <PieChart>
                  <Pie data={paymentPieData} cx="50%" cy="50%" innerRadius={45} outerRadius={65}
                    dataKey="value" stroke="none" paddingAngle={4}>
                    {paymentPieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              <div style={{ display: 'flex', justifyContent: 'center', gap: 20, marginTop: 8 }}>
                {paymentPieData.map((d, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: d.color }} />
                    <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{d.name}</span>
                    <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)' }}>{d.value}</span>
                  </div>
                ))}
              </div>
              {summary && (
                <div style={{ marginTop: 12, display: 'flex', justifyContent: 'space-between', background: 'var(--bg-elevated)', borderRadius: 'var(--radius-sm)', padding: '10px 14px' }}>
                  <div>
                    <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 2 }}>COD Revenue</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--accent-amber)' }}>₹{summary.cod_revenue.toLocaleString('en-IN')}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 2 }}>Prepaid Revenue</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--accent-blue)' }}>₹{summary.prepaid_revenue.toLocaleString('en-IN')}</div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)', fontSize: 13 }}>No data</div>
          )}
        </div>
      </div>

      {/* State-wise */}
      <div style={{ marginBottom: 22 }}>
        <div className="animate-in-4" style={{
          background: 'var(--bg-card)', border: '1px solid var(--border)',
          borderRadius: 'var(--radius-lg)', padding: 0, overflow: 'hidden',
        }}>
          <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8 }}>
            <MapPin size={15} color="var(--accent-green)" />
            <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>Orders by State</span>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)' }}>
                {['State', 'Orders', 'Revenue', 'Share'].map(h => (
                  <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 600 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {stateWise.slice(0, 10).map((s, i) => {
                const totalRev = stateWise.reduce((sum, x) => sum + x.revenue, 0);
                const pct = totalRev > 0 ? ((s.revenue / totalRev) * 100).toFixed(1) : '0';
                return (
                  <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '10px 16px', fontSize: 13, color: 'var(--text-primary)', fontWeight: 500 }}>{s.state || '—'}</td>
                    <td style={{ padding: '10px 16px', fontSize: 13, color: 'var(--text-secondary)' }}>{s.orders}</td>
                    <td style={{ padding: '10px 16px', fontSize: 13, color: 'var(--accent-amber)', fontWeight: 600 }}>₹{s.revenue.toLocaleString('en-IN')}</td>
                    <td style={{ padding: '10px 16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ flex: 1, background: 'var(--bg-elevated)', borderRadius: 3, height: 5, overflow: 'hidden', maxWidth: 60 }}>
                          <div style={{ width: `${pct}%`, height: '100%', background: COLORS[i % COLORS.length], borderRadius: 3 }} />
                        </div>
                        <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 500, minWidth: 35 }}>{pct}%</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {stateWise.length === 0 && <div style={{ textAlign: 'center', padding: 30, color: 'var(--text-muted)', fontSize: 13 }}>No state data</div>}
        </div>
      </div>

      {/* ── Top Selling ── */}
      <div style={{ marginTop: 28 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Tag size={15} color="var(--accent-amber)" />
            <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>Top Selling</span>
            <span style={{ fontSize: 12, color: 'var(--text-muted)', marginLeft: 4 }}>all orders excl. cancelled</span>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            {[3, 7, 15, 30].map(d => (
              <button key={d} onClick={() => setTsDays(d)} style={{
                padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                border: `1px solid ${tsDays === d ? 'rgba(79,125,249,0.3)' : 'var(--border)'}`,
                background: tsDays === d ? 'var(--accent-blue-soft)' : 'var(--bg-card)',
                color: tsDays === d ? 'var(--accent-blue)' : 'var(--text-secondary)',
                transition: 'var(--transition)',
              }}>{d}D</button>
            ))}
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>

          {/* Top Categories */}
          <div className="animate-in-4" style={{
            background: 'var(--bg-card)', border: '1px solid var(--border)',
            borderRadius: 'var(--radius-lg)', overflow: 'hidden',
          }}>
            <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8 }}>
              <BarChart3 size={14} color="var(--accent-amber)" />
              <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>Top Categories</span>
            </div>
            {topSelling && topSelling.top_categories.length > 0 ? (() => {
              const maxQty = Math.max(...topSelling.top_categories.map(c => c.qty_sold), 1);
              return (
                <div style={{ padding: '12px 0' }}>
                  {topSelling.top_categories.map((c, i) => (
                    <div key={i} style={{ padding: '8px 20px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5 }}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{c.category}</span>
                        <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
                          <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--accent-green)' }}>{c.qty_sold} units</span>
                          <span style={{ fontSize: 12, color: 'var(--accent-amber)', fontWeight: 600, minWidth: 80, textAlign: 'right' }}>₹{c.revenue.toLocaleString('en-IN')}</span>
                        </div>
                      </div>
                      <div style={{ height: 5, background: 'var(--bg-elevated)', borderRadius: 3, overflow: 'hidden' }}>
                        <div style={{
                          height: '100%', borderRadius: 3,
                          width: `${Math.round((c.qty_sold / maxQty) * 100)}%`,
                          background: COLORS[i % COLORS.length],
                          transition: 'width 0.4s ease',
                        }} />
                      </div>
                    </div>
                  ))}
                </div>
              );
            })() : (
              <div style={{ textAlign: 'center', padding: 30, color: 'var(--text-muted)', fontSize: 13 }}>No data</div>
            )}
          </div>

          {/* Top Sizes */}
          <div className="animate-in-4" style={{
            background: 'var(--bg-card)', border: '1px solid var(--border)',
            borderRadius: 'var(--radius-lg)', overflow: 'hidden',
          }}>
            <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8 }}>
              <Ruler size={14} color="var(--accent-blue)" />
              <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>Top Sizes</span>
              <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 4 }}>last 30 days</span>
            </div>
            {topSizes.length > 0 ? (() => {
              const maxQty = Math.max(...topSizes.map(s => s.qty_sold), 1);
              return (
                <div style={{ padding: '12px 0' }}>
                  {topSizes.map((s, i) => (
                    <div key={i} style={{ padding: '8px 20px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5 }}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{s.size}</span>
                        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--accent-blue)' }}>{s.qty_sold} units</span>
                      </div>
                      <div style={{ height: 5, background: 'var(--bg-elevated)', borderRadius: 3, overflow: 'hidden' }}>
                        <div style={{
                          height: '100%', borderRadius: 3,
                          width: `${Math.round((s.qty_sold / maxQty) * 100)}%`,
                          background: COLORS[i % COLORS.length],
                          transition: 'width 0.4s ease',
                        }} />
                      </div>
                    </div>
                  ))}
                </div>
              );
            })() : (
              <div style={{ textAlign: 'center', padding: 30, color: 'var(--text-muted)', fontSize: 13 }}>No data</div>
            )}
          </div>

          {/* Top Products */}
          <div className="animate-in-4" style={{
            background: 'var(--bg-card)', border: '1px solid var(--border)',
            borderRadius: 'var(--radius-lg)', overflow: 'hidden',
          }}>
            <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8 }}>
              <Package size={14} color="var(--accent-purple)" />
              <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>Top Products</span>
              <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 4 }}>top 20 by units sold</span>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)' }}>
                  {['#', 'Product', 'Category', 'Sold', 'Revenue'].map(h => (
                    <th key={h} style={{ padding: '9px 14px', textAlign: 'left', fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 600 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(topSelling?.top_products || []).map((p, i) => (
                  <tr key={i} onClick={() => openSizePopup(p.product)} style={{ borderBottom: '1px solid var(--border)', cursor: 'pointer' }}
                    onMouseEnter={e => (e.currentTarget as HTMLElement).style.background = 'var(--bg-elevated)'}
                    onMouseLeave={e => (e.currentTarget as HTMLElement).style.background = 'transparent'}>
                    <td style={{ padding: '9px 14px' }}>
                      <span style={{
                        width: 22, height: 22, borderRadius: 6, display: 'inline-flex',
                        alignItems: 'center', justifyContent: 'center', fontSize: 10.5, fontWeight: 700,
                        background: i < 3 ? 'var(--accent-amber-soft, rgba(245,166,35,0.12))' : 'var(--bg-elevated)',
                        color: i < 3 ? 'var(--accent-amber)' : 'var(--text-muted)',
                      }}>{i + 1}</span>
                    </td>
                    <td style={{ padding: '9px 14px', fontSize: 12.5, color: 'var(--text-primary)', fontWeight: 500, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.product}</td>
                    <td style={{ padding: '9px 14px' }}>
                      <span style={{
                        fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20,
                        background: 'var(--bg-elevated)', color: 'var(--text-secondary)',
                      }}>{p.category}</span>
                    </td>
                    <td style={{ padding: '9px 14px', fontSize: 13, color: 'var(--accent-green)', fontWeight: 700 }}>{p.qty_sold}</td>
                    <td style={{ padding: '9px 14px', fontSize: 12.5, color: 'var(--accent-amber)', fontWeight: 600 }}>₹{p.revenue.toLocaleString('en-IN')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {(!topSelling || topSelling.top_products.length === 0) && (
              <div style={{ textAlign: 'center', padding: 30, color: 'var(--text-muted)', fontSize: 13 }}>No data</div>
            )}
          </div>

        </div>
      </div>

      {/* Size Popup Modal */}
      {sizePopup && (
        <div onClick={() => setSizePopup(null)} style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            background: 'var(--bg-card)', border: '1px solid var(--border)',
            borderRadius: 'var(--radius-lg)', width: 380, maxHeight: '80vh',
            overflow: 'hidden', display: 'flex', flexDirection: 'column',
            boxShadow: 'var(--shadow-elevated)',
          }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sizePopup}</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>Units sold by size · last {tsDays} days</div>
              </div>
              <button onClick={() => setSizePopup(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 18, lineHeight: 1, padding: 4 }}>×</button>
            </div>
            <div style={{ overflowY: 'auto' }}>
              {sizeLoading ? (
                <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)', fontSize: 13 }}>Loading...</div>
              ) : sizeData.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)', fontSize: 13 }}>No size data found.</div>
              ) : (() => {
                const maxQty = Math.max(...sizeData.map((s: any) => s.qty_sold), 1);
                return sizeData.map((s: any, i: number) => (
                  <div key={i} style={{ padding: '12px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{
                      minWidth: 44, background: 'var(--accent-blue-soft)', color: 'var(--accent-blue)',
                      padding: '3px 10px', borderRadius: 6, fontSize: 12, fontWeight: 700, textAlign: 'center',
                    }}>{s.size}</span>
                    <div style={{ flex: 1, background: 'var(--bg-elevated)', borderRadius: 4, height: 8, overflow: 'hidden' }}>
                      <div style={{
                        width: `${(s.qty_sold / maxQty) * 100}%`, height: '100%',
                        background: 'var(--accent-green)', borderRadius: 4, transition: 'width 0.4s ease',
                      }} />
                    </div>
                    <span style={{ minWidth: 32, textAlign: 'right', fontSize: 14, fontWeight: 800, color: 'var(--text-primary)' }}>{s.qty_sold}</span>
                  </div>
                ));
              })()}
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Analytics;
